import theme from "@shared/utils/themeColors";
import React from "react";
import { Link } from "react-router-dom";
import { useCalendar } from "../../calendar/hooks/useCalendar";
import EventAttendeesAvatars from "./EventAttendeesAvatars";

/**
 * Component that displays upcoming events in a sidebar
 */
const toDateSafe = (ts: any): Date => {
  if (ts && typeof ts.toDate === "function") return ts.toDate();
  if (ts instanceof Date) return ts;
  return new Date(ts);
};

const UpcomingEvents: React.FC = () => {
  const { events } = useCalendar();
  const now = new Date();
  const upcoming = events
    .filter((ev) => {
      const date = toDateSafe(ev.startDate);
      return date >= now;
    })
    .sort((a, b) => {
      const da = toDateSafe(a.startDate);
      const db = toDateSafe(b.startDate);
      return da.getTime() - db.getTime();
    })
    .slice(0, 4);

  return (
    <div className={`${theme.bg.primary} p-4 rounded-xl`}>
      <div
        className={`text-xs font-semibold ${theme.text.inactive} uppercase mb-4`}
      >
        Upcoming Events
      </div>
      {upcoming.length === 0 ? (
        <div className="text-center py-6">No upcoming events</div>
      ) : (
        <ul>
          {upcoming.map((event) => {
            const start = toDateSafe(event.startDate);
            const dateStr = start.toLocaleDateString(undefined, {
              weekday: "short",
              day: "2-digit",
              month: "short",
            });
            return (
              <li key={event.id} className="relative pb-4 last-of-type:pb-0">
                <div className="pl-6">
                  <div
                    className={`text-xs font-medium uppercase ${theme.text.brand} mb-0.5`}
                  >
                    {dateStr}
                  </div>
                  <div className="text-sm mb-2">
                    <Link
                      className={`font-medium ${theme.text.primary} hover:text-gray-900 dark:hover:text-white`}
                      to={`/calendar/event/${event.id}`}
                    >
                      {event.title}
                    </Link>
                  </div>
                  {/* Attendee avatars */}
                  <EventAttendeesAvatars
                    attendees={event.attendees}
                    createdByProfile={event.createdByProfile}
                  />
                </div>
                {/* Timeline element */}
                <div aria-hidden="true">
                  <div
                    className={`absolute top-0.5 -bottom-1 left-0.5 ml-px w-0.5 ${theme.bg.tertiary}`}
                  />
                  <div
                    className={`absolute top-0.5 left-0 -ml-0.5 w-3 h-3 rounded-full ${theme.bg.inactive} border-2 ${theme.border.primary}`}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <div className="mt-4">
        <Link
          to="/calendar"
          className={`btn-sm w-full ${theme.bg.primary} ${theme.border.primary} hover:border-gray-300 dark:hover:border-gray-600 ${theme.text.secondary}`}
        >
          View All
        </Link>
      </div>
    </div>
  );
};

export default UpcomingEvents;
